<template>
  <template v-if="componentLoading">
    <Loading :style="style" />
  </template>
  <view v-else class="pft-list-3" :style="pftListComponentStyle" :flex="flex" ref="ScrollContainer">
    <view v-if="data.length === 0" class="empty-container">暂无数据</view>
    <view v-else :class="`scroll-container ${playStatus}`" :style="containerStyle" @mouseenter="onfocusenter" @mouseleave="onfocusleave">
      <RowTheme
        v-if="theme === 'row'"
        :data="data"
        :percent-style="percentStyle"
        :suffix="suffix"
        :nameStyle="nameStyle"
        :countStyle="countStyle"
        :rateStyle="rateStyle"
        :rateStyles="rateStyles"
        :rate-class="rateClass"
        v-model:scroll="shouldScroll"
        :progressStyles="progressStyles"
        :minHeight="ScrollContainerHeight"
      ></RowTheme>
      <DefaultTheme
        v-else
        :data="data"
        :percent-style="percentStyle"
        :suffix="suffix"
        :nameStyle="nameStyle"
        :countStyle="countStyle"
        :rateStyle="rateStyle"
        :rateStyles="rateStyles"
        :rate-class="rateClass"
        v-model:scroll="shouldScroll"
        :progressStyles="progressStyles"
        :minHeight="ScrollContainerHeight"
      />
    </view>
  </view>
</template>
<script lang="ts" setup>
import { ref, computed, watch } from "vue"
import Loading from "@p-v/packages/Loading.vue"
import DefaultTheme from "./List3Theme/default.vue"
import RowTheme from "./List3Theme/row.vue"

import { cloneDeep } from "lodash-es"
import { removePx } from "@p-v/utils"

const playStatus = ref("running")
const ScrollContainerHeight = ref(undefined)
const ScrollContainer = ref<any>(null)

const onfocusenter = () => {
  playStatus.value = "paused"
}

const onfocusleave = () => {
  playStatus.value = "running"
}

defineOptions({
  name: "List3",
})
interface List1Props {
  style?: { [key: string]: string }
  flex?: string
  suffix: string | undefined
  nameStyle?: { [key: string]: string }
  countStyle?: { [key: string]: string }
  percentStyle?: { [key: string]: string }
  rateStyle?: { [key: string]: string }
  rateStyles?: { [key: string]: string }[]
  progressStyles?: { [key: string]: string }
  duration?: number
  theme?: string
  scroll?: boolean
}

interface ListDataItem {
  name: string
  count: number
  percent: number
  rate?: number
}

const componentLoading = ref<boolean>(true)
const data = ref<ListDataItem[]>([])

const props = withDefaults(defineProps<List1Props>(), {
  style: () => ({}),
  flex: () => "dir:top cross:left",
  suffix: undefined,
  nameStyle: () => ({}),
  countStyle: () => ({}),
  rateStyle: () => ({}),
  rateStyles: () => [],
  duration: () => 10,
  theme: () => "default",
  scroll: () => true,
})

const shouldScroll = ref(true)

const containerStyle = computed(() => {
  if (!shouldScroll.value) {
    return {}
  }
  let progressHeight = 8
  let maxHeight = 32
  if (props.nameStyle.height || props.nameStyle.lineHeight) {
    maxHeight = Math.max(maxHeight, Number(removePx(props.nameStyle.lineHeight || props.nameStyle.height)))
  }
  if (props.countStyle.height || props.countStyle.lineHeight) {
    maxHeight = Math.max(maxHeight, Number(removePx(props.countStyle.lineHeight || props.countStyle.height)))
  }
  if (props.progressStyles?.height || props.progressStyles?.lineHeight) {
    progressHeight = Math.max(maxHeight, Number(removePx(props.progressStyles.lineHeight || props.progressStyles.height)))
  }
  if (props.scroll) {
    if (props.theme === "row") {
      const height = data.value.length * maxHeight
      const pages = Math.ceil(height / ScrollContainerHeight.value)
      return {
        animationDuration: `${props.duration * pages}s`,
      }
    } else {
      const height = data.value.length * (maxHeight + progressHeight)
      const pages = Math.ceil(height / ScrollContainerHeight.value)
      return {
        animationDuration: `${props.duration * pages}s`,
      }
    }
  }
  return {}
})

const rateClass = (item: ListDataItem, index: number) => {
  const styleTpl = props.rateStyles?.[index] || {}
  return Object.assign(cloneDeep(props.rateStyle), styleTpl, { width: `${item.percent}%` })
}

const pftListComponentStyle = computed(() => {
  const style: any = {}
  if (!props.scroll) {
    style.overflow = "auto"
  }
  return Object.assign(props.style, style)
})

const loading = () => {
  componentLoading.value = true
}

const setData = (val: ListDataItem[]) => {
  componentLoading.value = false
  data.value = val.map(e => {
    // 技术债，保持和 List1 的传值统一并，对历史已经使用 rate 的项目做兼容
    if (e.rate !== undefined && e.percent === undefined) {
      e.percent = e.rate
    }
    return {
      name: e.name,
      count: e.count,
      percent: e.percent,
      rate: e.rate,
    }
  })
}

if (props.scroll) {
  watch(
    () => ScrollContainer.value,
    val => {
      if (val) {
        console.log("ScrollContainer.value", ScrollContainer)
        ScrollContainerHeight.value = ScrollContainer.value?.clientHeight || 0
      }
    },
    { immediate: true },
  )
}

defineExpose({ loading, setData })
</script>
<style lang="scss" scoped>
@keyframes pft_rollup {
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(0, -50%, 0);
  }
}
.pft-list-3 {
  box-sizing: border-box;
  height: 100%;
  overflow: hidden;
  // 滚动条美化
  &::-webkit-scrollbar {
    width: 2px;
    height: 2px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(126, 126, 126, 0);
    border-radius: 1px;
    transition: all 0.3s;
  }
  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0);
  }
  &:hover::-webkit-scrollbar-thumb {
    background-color: rgba(126, 126, 126, 0.5);
  }
  .scroll-container {
    animation-name: pft_rollup;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    animation-play-state: running; /* 动画默认运行 */
    &.paused {
      animation-play-state: paused;
    }
  }
  .empty-container {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    color: rgba(163, 166, 173, 0.6431372549);
  }
}
</style>
